import type { WorkbookInstance } from '@fortune-sheet/react';
import * as core from '@fortune-sheet/core';
import type { Context } from '@fortune-sheet/core';

/**
 * The vendor-internal refs `patches/@fortune-sheet+react+1.0.4.patch` exposes
 * via `getRefs()` — the same DOM/mutable-ref handles the library's own
 * toolbar actions close over (e.g. `core.handleSum` at `react:6267`).
 */
export interface WorkbookRefs {
    cellInput: HTMLDivElement | null;
    fxInput: HTMLDivElement | null;
    globalCache: unknown;
    workbookContainer: HTMLDivElement | null;
    scrollbarX: HTMLDivElement | null;
    scrollbarY: HTMLDivElement | null;
}

/**
 * The escape-hatch surface the react patch adds to the Workbook ref.
 * Every shim that needs library-internal state or a raw `setContextWithProduce`
 * goes through this — never a direct cast of `workbookRef.current`.
 */
export interface WorkbookInternals {
    getContext: () => Context;
    /** `options` (e.g. `{ insertRowColOp }` / `{ deleteRowColOp }`) is forwarded
     *  verbatim to the library's own `setContextWithProduce` — the patch
     *  exposes that function directly, and it already accepts a second
     *  argument (react:10977-10978), so widening this type is the only
     *  change needed; undo/redo then take the SAME structural-op path
     *  (formulaCache reset) the library's own insert/delete menus use. */
    setContextWithProduce: (recipe: (draft: Context) => void, options?: Record<string, unknown>) => void;
    getRefs: () => WorkbookRefs;
}

type PatchedWorkbookInstance = WorkbookInstance & Partial<WorkbookInternals>;

/** Fortune's formula dependency map (`ctx.formulaCache.formulaCellInfoMap`, non-null). */
export type FormulaCellInfoMap = NonNullable<Context['formulaCache']['formulaCellInfoMap']>;

/** True when a cell is currently being edited — the single "is editing" check
 *  shared by the Esc policy, Lane A's point-mode/F4 gate, and the Ctrl+S
 *  commit-before-save step. `ctx` is `undefined` when internals are absent. */
export const isEditingInContext = (ctx: Context | undefined): boolean => (ctx?.luckysheetCellUpdate?.length ?? 0) > 0;

/** The library's own filter-menu popover class — shared by the Esc policy
 *  (a live one blocks `close-menu`) and the wheel shim (a live one is modal). */
export const FILTER_MENU_SELECTOR = '.fortune-filter-menu';

/** True when `rect` intersects the viewport — shared "is this popover
 *  actually visible" check for a stale/off-screen filter menu. */
export const isRectVisible = (rect: DOMRect): boolean =>
    rect.bottom > 0 && rect.right > 0 && rect.top < window.innerHeight && rect.left < window.innerWidth;

/** True when a live (visible, not stale/off-screen) filter menu exists under `root`. */
export const isFilterMenuVisible = (root: ParentNode): boolean => {
    const menu = root.querySelector(FILTER_MENU_SELECTOR);
    return menu ? isRectVisible(menu.getBoundingClientRect()) : false;
};

/**
 * Feature-detects the patch (`patches/@fortune-sheet+react+1.0.4.patch`).
 * Returns `null` when the patch is missing (fresh install before
 * `postinstall` ran, or a future upgrade that drops it) — every caller must
 * treat `null` as "do not intercept this key", never throw.
 */
export const getInternals = (wb: WorkbookInstance | null | undefined): WorkbookInternals | null => {
    const patched = wb as PatchedWorkbookInstance | null | undefined;
    if (
        typeof patched?.getContext === 'function' &&
        typeof patched?.setContextWithProduce === 'function' &&
        typeof patched?.getRefs === 'function'
    ) {
        return { getContext: patched.getContext, setContextWithProduce: patched.setContextWithProduce, getRefs: patched.getRefs };
    }
    return null;
};

/** Runs `recipe` on the live context with no undo entry and no onOp (bookkeeping, not a
 *  user edit); `fallback` is the equivalent public API for an unpatched Workbook. */
export const produceQuietly = (wb: WorkbookInstance, recipe: (ctx: Context) => void, fallback: () => void): void => {
    const internals = getInternals(wb);
    if (internals) internals.setContextWithProduce(recipe, { noHistory: true });
    else fallback();
};

export type ApiCall = Parameters<WorkbookInstance['batchCallApis']>[0][number];

/** batchCallApis without an undo entry or onOp — see produceQuietly. */
export const batchCallApisQuietly = (wb: WorkbookInstance, calls: ApiCall[]): void => {
    const api = core.api as unknown as Record<string, (ctx: Context, ...args: unknown[]) => void>;
    produceQuietly(
        wb,
        (ctx) => calls.forEach(({ name, args }) => api[name](ctx, ...args)),
        () => wb.batchCallApis(calls),
    );
};

// core's internal helpers below ship in the bundle (`exports.<name>`) but are
// not part of its public .d.ts — narrow with a cast, same pattern as
// `fortuneSheetSetup.ts`'s `locale` narrowing. Each is existence-checked at
// load: a library upgrade that drops one only turns formula point-mode
// off, it never throws.
const coreInternal = core as unknown as {
    handleSum?: (...args: any[]) => void;
    createFilter?: (...args: any[]) => void;
    clearFilter?: (...args: any[]) => void;
    rangeSetValue?: (...args: any[]) => void;
    israngeseleciton?: (...args: any[]) => boolean;
    handleFormulaInput?: (...args: any[]) => void;
    updateCell?: (...args: any[]) => void;
    rowLocationByIndex?: (...args: any[]) => [number, number, number];
    colLocationByIndex?: (...args: any[]) => [number, number, number];
    createRangeHightlight?: (...args: any[]) => void;
    createFormulaRangeSelect?: (...args: any[]) => void;
    rangeHightlightselected?: (...args: any[]) => void;
    /** Core's SSF format export — number-format validation
     *  (`isSafeNumFmt`), the Sum/Average status-bar formatter, and the
     *  Format Cells live preview all share this one feature-detection point
     *  rather than each casting `@fortune-sheet/core` ad hoc. */
    update?: (fmt: string, v: unknown) => unknown;
    /** True-number test — shared by the status bar and the Format
     *  Cells / Paste Special `m` recompute so a numeric string is coerced the
     *  same way everywhere. */
    isRealNum?: (v: unknown) => boolean;
    /** Re-wraps `txt` (a formula string) into the library's own span-per-token
     *  markup — F4 must reformat through this, never a flat
     *  `textContent` write, or the caret's flattened offset stops matching a
     *  real span/text-node position. */
    functionHTMLGenerate?: (txt: string) => string;
    /** The library's own "typed value -> {m, ct, v}" classifier — external
     *  Paste Special text goes through this so a numeric
     *  token lands as a number, exactly like a native paste. */
    genarate?: (value: string) => [string | number, { fa?: string; t?: string }, string | number | boolean] | null;
    /** Grows a sheet's runtime `data` matrix in place, padding every existing
     *  row with `columnsToAdd` more `null` cells and appending `rowsToAdd`
     *  new full-width rows — the same growth Fortune's own paste uses so a
     *  block pasted past the current extent never leaves holey rows. */
    expandRowsAndColumns?: (data: unknown[][], rowsToAdd: number, columnsToAdd: number) => unknown[][];
    /** Moves the active cell within the current selection ('down'/'right',
     *  negative `index` for the reverse direction) — Excel's own Enter/Tab
     *  move-within-selection behaviour. */
    moveHighlightCell?: (ctx: Context, postion: 'down' | 'right', index: number, type: 'rangeOfSelect') => void;
    /** Returns the toolbar's own click handler for a `TOOLBAR_ITEMS` name
     *  (e.g. `'italic'`), the same lookup the toolbar buttons themselves use —
     *  shared so a keyboard shortcut (Ctrl+I/U) reaches the identical path. */
    toolbarItemClickHandler?: (name: string) => ((ctx: Context, cellInput: HTMLDivElement | null) => void) | undefined;
    /** Inserts `count` rows/columns above/left of `index` (`'lefttop'`) — the
     *  library's own Insert, reached directly for Ctrl+Shift+=. */
    insertRowCol?: (
        ctx: Context,
        op: { type: 'row' | 'column'; index: number; count: number; direction: 'lefttop' | 'rightbottom' },
    ) => void;
    /** Deletes rows/columns `start`-`end` inclusive — the library's own
     *  Delete, reached directly for Ctrl+-. */
    deleteRowCol?: (ctx: Context, op: { type: 'row' | 'column'; start: number; end: number }) => void;
    /** Hides every row/column in the current (single-range) selection. */
    hideSelected?: (ctx: Context, type: 'row' | 'column') => void;
    /** Un-hides every row/column spanned by the current selection. */
    showSelected?: (ctx: Context, type: 'row' | 'column') => void;
    /** Opens the insert/edit-link card for the active selection. */
    handleLink?: (ctx: Context) => void;
    /** `1` -> `yyyy-MM-dd` (today), `2` -> `yyyy-MM-dd HH:mm:ss` (now) —
     *  Ctrl+; uses format 1; format 2 is never used (the library's own
     *  date+time stamp is replaced by a separate, app-owned time-only insert). */
    getNowDateTime?: (format: 1 | 2) => string;
    /** Recalculates every formula cell that depends on (r, c) — the same
     *  dependent-recalc trigger `updateCell`'s own plain-value commit path
     *  uses internally (core:60973-60981), reachable standalone for a write
     *  that goes straight to `sheet.data` instead of through `updateCell`
     *  (Paste Special's Values/Formats modes). */
    execFunctionGroup?: (ctx: Context, r: number, c: number, value: unknown) => void;
    /** Removes (r, c)'s own calcChain entry — the same cleanup `updateCell`'s
     *  plain-value commit path runs (core:60978) right before
     *  `execFunctionGroup`, whenever it overwrites a formula cell with a
     *  literal, so the cell stops being re-evaluated on the next recalc.
     *  Reachable standalone for the same reason `execFunctionGroup` is: a
     *  write that goes straight to `sheet.data` instead of through
     *  `updateCell` (Ctrl+Enter fill's literal branch). */
    delFunctionGroup?: (ctx: Context, r: number, c: number, id?: string) => void;
    /** Moves the caret to the end of `el`'s text content — the same helper
     *  the library's own InputBox layout effect uses right after seeding a
     *  cell's edit text (core:77116, deferred one tick). */
    moveToEnd?: (el: HTMLElement) => void;
    /** True when the rectangle [r1,r2] x [c1,c2] partially overlaps an
     *  existing merge — the same check native paste/fill refuse on (core
     *  :79218-79222, :79413-79418). Reads `ctx.config.merge` directly
     *  (the `cfg` argument is accepted but unused by the library itself). */
    hasPartMC?: (ctx: Context, cfg: Context['config'], r1: number, r2: number, c1: number, c2: number) => boolean;
    /** The core patch's background builds of what an edit's formula pass needs (aica-patch:formula-links-incremental):
     *  the formula map, against the committed context into a private map (`result()` null when it cannot), and its
     *  dependency links. Both step a slice at a time; sheetHydration.ts runs them after a workbook settles. */
    createFormulaMapBuildJob?: (ctx: Context) => {
        step: () => boolean;
        result: () => { map: FormulaCellInfoMap; cache: Record<string, unknown> } | null;
    };
    createFormulaLinkJob?: (map: FormulaCellInfoMap) => { step: () => boolean };
    /** Recomputes the row/column pixel offsets and scroll extent from the current sheet's config —
     *  what Fortune does in an effect after a row height changes; reached inside the same update
     *  when the scroll position must move with the new offsets (row auto-fit on open). */
    updateContextWithSheetData?: (ctx: Context, data: unknown[][]) => void;
};

/** The point-mode mirror set — used by
 *  `formulaEditActions.ts`. Re-exported here (not imported ad hoc from
 *  `@fortune-sheet/core`) so every internal-dependent action shares one
 *  feature-detection point. */
export const {
    handleSum,
    createFilter,
    clearFilter,
    rangeSetValue,
    israngeseleciton,
    handleFormulaInput,
    updateCell,
    rowLocationByIndex,
    colLocationByIndex,
    createRangeHightlight,
    createFormulaRangeSelect,
    rangeHightlightselected,
    update,
    isRealNum,
    functionHTMLGenerate,
    genarate,
    expandRowsAndColumns,
    moveHighlightCell,
    toolbarItemClickHandler,
    insertRowCol,
    deleteRowCol,
    hideSelected,
    showSelected,
    handleLink,
    getNowDateTime,
    execFunctionGroup,
    delFunctionGroup,
    moveToEnd,
    hasPartMC,
    createFormulaMapBuildJob,
    createFormulaLinkJob,
} = coreInternal;

/** See `updateContextWithSheetData` above; a no-op if the library no longer exports it. */
export const refreshRowColOffsets = (ctx: Context, data: unknown[][]): void => coreInternal.updateContextWithSheetData?.(ctx, data);

/**
 * Full formula→literal overwrite cleanup, mirroring what `updateCell` itself
 * does when a formula cell is overwritten by a literal: core:60978's
 * `delFunctionGroup` (the cell's calcChain entry) PLUS core:61074's
 * unconditional `setFormulaCellInfo(ctx, {r, c, id})` — which, for a cell
 * whose formula is now nil, just deletes its `formulaCache.formulaCellInfoMap`
 * entry (core:55931-55938). That function isn't part of the package's public
 * export surface, so the one line it takes for our case (delete the
 * `r{r}c{c}i{id}` key) is replicated directly rather than reached through it.
 * `delFunctionGroup` alone does NOT touch this map — it is a separate,
 * lazily-built cache `execFunctionGroup` consults for dependency edits, so
 * without this second half a cleared calcChain entry can still resurrect its
 * old formula on a later dependency edit. Reachable standalone for the same
 * reason `execFunctionGroup` is: a write that goes straight to `sheet.data`
 * instead of through `updateCell` (Ctrl+Enter fill's literal branch, Paste
 * Special's Values/All-with-literal-source). `sheetId` is passed through to
 * BOTH halves — `delFunctionGroup` otherwise defaults to `ctx.currentSheetId`,
 * which would silently target the wrong sheet's calcChain if `sheetId` ever
 * isn't the active one. Order relative to the destination cell's own write
 * doesn't matter — this deletes the map key directly rather than re-deriving
 * it from the cell's current formula, unlike `setFormulaCellInfo` itself.
 */
export const clearFormulaAt = (ctx: Context, r: number, c: number, sheetId: string): void => {
    delFunctionGroup?.(ctx, r, c, sheetId);
    if (ctx.formulaCache.formulaCellInfoMap) delete ctx.formulaCache.formulaCellInfoMap[`r${r}c${c}i${sheetId}`];
};

/**
 * Commits any in-progress cell edit via the library's own `updateCell` recipe
 * (the same one the InputBox commits on Enter/Tab), then invokes `then` one
 * animation frame later — long enough for the resulting state update (and the
 * `onOp` it drives) to land. A no-op that calls `then` synchronously when
 * nothing is being edited, or internals are unavailable (patch missing, or a
 * read-only history preview where nothing is editable). Returns a promise that
 * resolves once `then` itself has settled — awaiting its own returned
 * promise when it's async — for a caller that needs to sequence something
 * after it (e.g. opening Version History only once any pending edit is
 * committed AND flushed); existing callers that don't await it are
 * unaffected. Shared by the Ctrl+S save shortcut and every drawer-
 * close path that isn't an Esc-cancel — Esc's own library-cancel
 * semantics are intentionally left alone.
 */
export const commitEditThen = (internals: WorkbookInternals | null, then: () => void | Promise<void>): Promise<void> => {
    if (!internals || !isEditingInContext(internals.getContext())) {
        return Promise.resolve(then());
    }
    internals.setContextWithProduce((draft) => {
        const refs = internals.getRefs();
        updateCell?.(draft, draft.luckysheetCellUpdate[0], draft.luckysheetCellUpdate[1], refs.cellInput);
    });
    return new Promise((resolve, reject) =>
        requestAnimationFrame(() => {
            try {
                resolve(then());
            } catch (err) {
                // A synchronously-throwing `then` must reject, not leave the
                // promise pending forever.
                reject(err);
            }
        }),
    );
};

/**
 * Recomputes a cell's cached display string `m` for `fa`, exactly as the
 * public setCellFormat does (core:76331) — the ONE place Format Cells
 * and Paste Special's Formats merge recompute `m`, so
 * they can't drift into two slightly different coercion rules. `fa` is
 * assumed already validated (isSafeNumFmt); returns `undefined` (keep the
 * caller's prior `m`) when `v` is nil or the format throws.
 */
export const recomputeDisplayValue = (fa: string, v: unknown): string | undefined => {
    if (v == null) return undefined;
    const x = fa !== '@' && isRealNum?.(v) ? Number(v) : v;
    try {
        return String(update!(fa, x));
    } catch {
        return undefined;
    }
};

/** Fail-closed partial-merge check shared by `fillSelection` and
 *  `applyPastePlan`: true when the rectangle [r1,r2] x [c1,c2] partially
 *  overlaps an existing merge, OR when the library dropped the `hasPartMC`
 *  internal entirely (a version bump) — silently falling through there would
 *  let a fill/paste unmerge a cell the grid still renders as merged, with no
 *  way left to detect it. */
export const isPartiallyMerged = (ctx: Context, r1: number, r2: number, c1: number, c2: number): boolean =>
    !hasPartMC || hasPartMC(ctx, ctx.config, r1, r2, c1, c2);

/** True only when every point-mode mirror function is present (this also
 *  gates F4's `functionHTMLGenerate` reformat — one feature-detection
 *  point for both). A library upgrade that renames/drops one of these turns
 *  point mode/F4 off rather than throwing at call time. */
export const hasPointModeSupport: boolean = [
    handleSum,
    createFilter,
    clearFilter,
    rangeSetValue,
    israngeseleciton,
    handleFormulaInput,
    updateCell,
    rowLocationByIndex,
    colLocationByIndex,
    createRangeHightlight,
    createFormulaRangeSelect,
    rangeHightlightselected,
    functionHTMLGenerate,
].every((fn) => typeof fn === 'function');
